import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class ResolutionService {
    private screenSizeSubject: BehaviorSubject<{ width: number; height: number; mobile: boolean }>;
    public screenSize$: Observable<{ width: number; height: number; mobile: boolean }>;
    public isMobileResolution$: Observable<boolean>;

    constructor(private zone: NgZone) {
        const initialWidth = window.innerWidth;
        const initialHeight = window.innerHeight;
        const initialMobile = initialWidth <= 992;

        // Initialize BehaviorSubject with the initial screen size
        this.screenSizeSubject = new BehaviorSubject<{ width: number; height: number; mobile: boolean }>({
            width: initialWidth,
            height: initialHeight,
            mobile: initialMobile
        });

        // Observable to track both width and height changes
        this.screenSize$ = fromEvent(window, 'resize').pipe(
            debounceTime(0), // Prevent rapid updates
            map(() => ({
                width: window.innerWidth,
                height: window.innerHeight,
                mobile: window.innerWidth <= 992
            })),
            startWith({
                width: initialWidth,
                height: initialHeight,
                mobile: initialMobile
            }) // Start with the initial screen size
        );

        // Update the BehaviorSubject whenever the screen size changes
        this.screenSize$.subscribe((size) => {
            this.zone.run(() => {
                this.screenSizeSubject.next(size);
            });
        });

        // Derived observable to determine if the screen is mobile
        this.isMobileResolution$ = this.screenSize$.pipe(
            map(({width}) => width <= 992) // Define mobile based on width
        );
    }

    // Optional: Expose a synchronous method to get the current screen size
    get currentSize(): { width: number; height: number; mobile: boolean } {
        return this.screenSizeSubject.getValue();
    }

    // Optional: Expose a synchronous method to check if the screen is mobile
    get isMobileResolution(): boolean {
        const currentSize = this.screenSizeSubject.getValue();
        return currentSize ? currentSize.mobile : false;
    }
}
